/**
 * Karma — shopping / coupon / cashback assistant
 *
 * Exposes a single WAR group to every origin with no `use_dynamic_url`, globbing
 * `img/*.*`, `styles/*.*`, `js/*.*`, `fonts/*.*`, `lottie/*.*` and `rules/*.*`. The
 * globs resolve to stable vendor-named files, so each is fetchable from any page at a
 * fixed URL.
 *
 * `img/logo_prod_supported.png` is the cleanest signal: being an image it loads through
 * `probeImage` with no CORS grant, and the vendor-named `hermesSDK` assets corroborate.
 *
 * Manifest verified against version 10.88.0.21975.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'emalgedpdlghbkikiaeocoblajamonoh';

export const karma: ExtensionDefinition = {
  id: ID,
  name: 'Karma | Online shopping, but better',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 400_000,
  notes:
    'Images, styles and scripts globbed to every origin, no dynamic URL. The logo image ' +
    'is conclusive; the hermesSDK assets corroborate.',
  signals: [
    {
      id: 'war-logo-prod-image',
      kind: 'web-accessible-resource',
      description:
        'Load img/logo_prod_supported.png via <img> — a static image exposed to every ' +
        'origin; needs no CORS grant, so it works under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'img/logo_prod_supported.png'),
    },
    {
      id: 'war-hermes-sdk-css',
      kind: 'web-accessible-resource',
      description:
        'Fetch styles/hermesSDK.css — a vendor-named stylesheet, distinctive to Karma.',
      weight: 0.5,
      run: () => probeFetch(ID, 'styles/hermesSDK.css'),
    },
    {
      id: 'war-hermes-sdk-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch js/hermesSDK.js — the matching vendor-named script, an independent path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'js/hermesSDK.js'),
    },
  ],
};
